import { BusinessPlan, SectionIdEnum } from '../../../types/businessPlan';

// Financial data interface for charts
export interface FinancialData {
  // Revenue vs Expenses chart data
  revenueExpenses: {
    labels: string[];
    revenue: number[];
    expenses: number[];
  };
  // Profit trend chart data
  profitTrend: {
    labels: string[];
    profit: number[];
  };
  // Revenue breakdown chart data
  revenueBreakdown: {
    labels: string[];
    values: number[];
  };
  // Cash flow chart data
  cashFlow: {
    labels: string[];
    values: number[];
  };
  // Original data for backward compatibility
  revenue: number[];
  expenses: number[];
  profit: number[];
  periods: string[];
}

// Market data interface for charts
export interface MarketData {
  // Market share chart data
  marketShare: {
    labels: string[];
    values: number[];
  };
  // Target market segments chart data
  marketSegments: {
    labels: string[];
    values: number[];
  };
  // SWOT analysis chart data
  swotChart: {
    labels: string[];
    values: number[];
  };
  // Growth trend chart data
  growthTrend: {
    labels: string[];
    values: number[];
  };
  // Original data for backward compatibility
  marketSize: number;
  competitors: Array<{ name: string; marketShare: number }>;
  targetMarketSegments: Array<{ name: string; size: number }>;
  swotAnalysis: {
    strengths: number;
    weaknesses: number;
    opportunities: number;
    threats: number;
  };
}

// Default financial data for when we can't extract real data
const DEFAULT_FINANCIAL_DATA: FinancialData = {
  revenueExpenses: {
    labels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
    revenue: [50000, 75000, 125000, 200000, 300000],
    expenses: [40000, 55000, 85000, 120000, 180000],
  },
  profitTrend: {
    labels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
    profit: [10000, 20000, 40000, 80000, 120000],
  },
  revenueBreakdown: {
    labels: ['Product A', 'Product B', 'Product C', 'Services'],
    values: [40, 20, 15, 25],
  },
  cashFlow: {
    labels: ['Q1', 'Q2', 'Q3', 'Q4', 'Q1', 'Q2', 'Q3', 'Q4'],
    values: [5000, 15000, 10000, 20000, 25000, 30000, 35000, 45000],
  },
  // Original data for backward compatibility
  revenue: [50000, 75000, 125000, 200000, 300000],
  expenses: [40000, 55000, 85000, 120000, 180000],
  profit: [10000, 20000, 40000, 80000, 120000],
  periods: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5']
};

// Default market analysis data
const DEFAULT_MARKET_DATA: MarketData = {
  marketShare: {
    labels: ['Competitor A', 'Competitor B', 'Competitor C', 'Others', 'Our Company'],
    values: [30, 25, 15, 20, 10],
  },
  marketSegments: {
    labels: ['Segment 1', 'Segment 2', 'Segment 3', 'Segment 4'],
    values: [40, 30, 20, 10],
  },
  swotChart: {
    labels: ['Strengths', 'Weaknesses', 'Opportunities', 'Threats'],
    values: [8, 4, 7, 5],
  },
  growthTrend: {
    labels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
    values: [5, 12, 18, 25, 32],
  },
  // Original data for backward compatibility
  marketSize: 100,
  competitors: [
    { name: 'Competitor A', marketShare: 30 },
    { name: 'Competitor B', marketShare: 25 },
    { name: 'Competitor C', marketShare: 15 },
    { name: 'Others', marketShare: 20 },
    { name: 'Our Company', marketShare: 10 }
  ],
  targetMarketSegments: [
    { name: 'Segment 1', size: 40 },
    { name: 'Segment 2', size: 30 },
    { name: 'Segment 3', size: 20 },
    { name: 'Segment 4', size: 10 }
  ],
  swotAnalysis: {
    strengths: 8,
    weaknesses: 4,
    opportunities: 7,
    threats: 5
  }
};

/**
 * Attempts to extract financial data from the business plan content
 * Falls back to default data if extraction fails
 */
export const extractFinancialData = (plan: BusinessPlan): FinancialData => {
  try {
    // Try to find financial section content
    const financialSection = plan.sections?.[SectionIdEnum.FINANCIALS];
    if (!financialSection?.content) {
      return DEFAULT_FINANCIAL_DATA;
    }
    
    // For now, we'll use default data
    // In a real implementation, we would parse the content to extract actual numbers
    // This would involve NLP or pattern matching to find financial projections
    return DEFAULT_FINANCIAL_DATA;
  } catch (error) {
    console.error('Error extracting financial data:', error);
    return DEFAULT_FINANCIAL_DATA;
  }
};

/**
 * Attempts to extract market analysis data from the business plan content
 * Falls back to default data if extraction fails
 */
export const extractMarketData = (plan: BusinessPlan): MarketData => {
  try {
    // Try to find market analysis section content
    const marketSection = plan.sections?.[SectionIdEnum.MARKET_RESEARCH];
    if (!marketSection?.content) {
      return DEFAULT_MARKET_DATA;
    }
    
    // For now, we'll use default data
    // In a real implementation, we would parse the content to extract actual market information
    return DEFAULT_MARKET_DATA;
  } catch (error) {
    console.error('Error extracting market data:', error);
    return DEFAULT_MARKET_DATA;
  }
};
